import { useIsFetching, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, stripSearchParams } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import { ApiError } from "../../api/client";
import { directoryPageQueryOptions, useRelatedParties } from "../../api/directory";
import { sessionQueryOptions, useSession } from "../../api/session";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { buttonClassName } from "../../components/ui/button-styles";
import { Card, CardHeader } from "../../components/ui/card";
import { Empty } from "../../components/ui/empty";
import { Input } from "../../components/ui/field";
import { FilterPill, Toolbar } from "../../components/ui/filter-pill";
import { Pager } from "../../components/ui/pager";
import { Badge, Pill } from "../../components/ui/pill";
import { Table, type TableColumn } from "../../components/ui/table";
import { Updating } from "../../components/ui/updating";
import {
	DIRECTORY_QUERY_MAX_LENGTH,
	PARTY_KIND_DOT_CLASS,
	PARTY_KIND_LABELS,
	PARTY_KINDS,
	PARTY_STANDING_TONES,
	partyStatus,
	type DirectoryEntry,
	type PartyKind,
} from "../../domain/directory";
import { cn } from "../../lib/cn";
import { formatShortDate } from "../../lib/format";
import { AddPartyDrawer } from "./-components/add-party-drawer";
import { NoAccess } from "./-components/no-access";
import { PartyLink } from "./-components/party-link";

/** The page's one-line explainer, which the no-access page and a party's own page show as well. */
export const DIRECTORY_DESCRIPTION =
	"Every party the business has a relationship with, whatever record each one is kept as.";

/** What a column shows for a value the record does not name. */
const UNKNOWN = "—";

const directorySearchSchema = z.object({
	kind: z.enum(PARTY_KINDS).optional().catch(undefined),
	q: z.string().default("").catch(""),
	/** A page link from the API, which the page never reads; the first page when there is none. */
	page: z.string().optional().catch(undefined),
	/** "new" opens the drawer that adds a party. */
	party: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/directory/")({
	validateSearch: directorySearchSchema,
	search: { middlewares: [stripSearchParams({ q: "" })] },
	loaderDeps: ({ search: { kind, q, page } }) => ({ kind, q, page }),
	loader: async ({ context, deps }) => {
		// A member with no access to the Directory is told so by the page; asking the API would only answer 403.
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null || session.member.access.directory === "none") {
			return;
		}
		try {
			await context.queryClient.query({ ...directoryPageQueryOptions(deps), staleTime: "static" });
		} catch (error) {
			// A page link the API no longer takes, from an old bookmark or edited by hand: start at the first page.
			if (deps.page !== undefined && error instanceof ApiError && error.status === 400) {
				throw redirect({ to: "/directory", search: {}, replace: true });
			}
			throw error;
		}
	},
	head: () => ({ meta: [{ title: "Directory · Ravnar" }] }),
	staticData: { crumbs: ["Directory"] },
	component: DirectoryPage,
});

/** How a party's name reads in the table, whichever record owns it. */
const PARTY_NAME_CLASS = "block font-medium text-ink hover:underline";

const columns: readonly TableColumn<DirectoryEntry>[] = [
	{
		key: "name",
		header: "Party",
		cell: entry => (
			<span className="flex items-center gap-2.5">
				<span
					aria-hidden="true"
					className={cn("h-[7px] w-[7px] shrink-0 rounded-full", PARTY_KIND_DOT_CLASS[entry.kind])}
				/>
				<span className="min-w-0">
					<PartyLink kind={entry.kind} id={entry.id} className={PARTY_NAME_CLASS}>
						{entry.name}
					</PartyLink>
					<span className="block text-caption text-ink-3">{entry.detail}</span>
				</span>
			</span>
		),
	},
	{ key: "kind", header: "Kind", cell: entry => <Badge>{PARTY_KIND_LABELS[entry.kind]}</Badge> },
	{ key: "contact", header: "Contact", muted: true, cell: entry => entry.email ?? UNKNOWN },
	{ key: "country", header: "Country", muted: true, cell: entry => entry.country ?? UNKNOWN },
	{
		key: "since",
		header: "Since",
		align: "right",
		muted: true,
		// An incomplete party the Directory agent added may not have a start date yet.
		cell: entry => (entry.since === undefined ? UNKNOWN : formatShortDate(entry.since)),
	},
	{
		key: "status",
		header: "Status",
		align: "right",
		cell: entry => {
			const { standing, label } = partyStatus(entry);
			return <Pill tone={PARTY_STANDING_TONES[standing]}>{label}</Pill>;
		},
	},
];

interface PartyListProps {
	/** Only an editor of the Directory sees Add party and the drawer that opens it. */
	readonly canAdd: boolean;
}

function PartyList({ canAdd }: PartyListProps) {
	const { kind, q, page, party } = Route.useSearch();
	const navigate = Route.useNavigate();
	// What has been typed into the search box. The box keeps it rather than reading it back from the URL: the API
	// answers the search now, so the navigation a keystroke starts only arrives once the answer does, and a box fed
	// from the URL would put the older text back over the newer and drop whatever was typed in between —
	// `e2e/work.spec.ts` types into it fast enough to catch that. The list is still read from the URL alone, and the
	// box starts from it, so a link into the page with a `q` opens showing its search.
	const [typed, setTyped] = useState(q);
	// A search that reaches the URL while nobody is typing in the box came from somewhere else — the rail's search box
	// opens this page with what it holds, even with the page open already — so the box takes it. While the box has
	// focus, a new `q` is only a keystroke of its own arriving, and the box holds that already or has typed past it.
	const [isTyping, setIsTyping] = useState(false);
	const [shownQ, setShownQ] = useState(q);
	if (q !== shownQ) {
		setShownQ(q);
		if (!isTyping) {
			setTyped(q);
		}
	}
	const directoryQuery = directoryPageQueryOptions({ kind, q, page });
	const { data: directoryPage } = useSuspenseQuery(directoryQuery);
	// The counts cover every party of the organization, whichever page and whichever filter this is, so the pills
	// keep saying how many there are of each kind while one of them is selected.
	const everyKind = PARTY_KINDS.reduce((sum, option) => sum + directoryPage.counts[option], 0);
	const { total } = directoryPage;
	const isUpdating = useIsFetching({ queryKey: directoryQuery.queryKey, exact: true }) > 0;
	const isAdding = party === "new" && canAdd;
	// The parties whose names hold the search come first, A→Z and paged; the ones the Directory's knowledge base finds
	// most like it follow them, on the page where they end, once the box has rested.
	const relatedParties = useRelatedParties({ q, kind, namesEnd: directoryPage.next === null });

	const closeDrawer = () => {
		void navigate({ search: previous => ({ ...previous, party: undefined }), replace: true });
	};

	/** Shows a kind, or every kind. A filtered list's page links do not survive the filter changing, so page goes. */
	function showKind(option: PartyKind | undefined) {
		void navigate({ search: previous => ({ ...previous, kind: option, page: undefined }), replace: true });
	}

	return (
		<>
			<PageHeader
				title="Directory"
				description={DIRECTORY_DESCRIPTION}
				actions={
					canAdd ? (
						<Button
							variant="primary"
							onClick={() => {
								void navigate({ search: previous => ({ ...previous, party: "new" }) });
							}}
						>
							Add party
						</Button>
					) : undefined
				}
			/>
			<Toolbar
				end={
					<div className="w-[200px]">
						<Input
							aria-label="Search parties"
							placeholder="Search parties"
							// As long a search as the API takes (`directoryQuerySchema.q`), so the box can never ask
							// it something it answers with 400 — the old page filtered in the browser and had no
							// limit, and a pasted paragraph would replace the list with an error.
							maxLength={DIRECTORY_QUERY_MAX_LENGTH}
							value={typed}
							onFocus={() => {
								setIsTyping(true);
							}}
							onBlur={() => {
								setIsTyping(false);
							}}
							onChange={event => {
								const text = event.target.value;
								setTyped(text);
								// The API answers the search, so every keystroke starts the list again from its first
								// page: a page link the old list handed out names a party this one may not hold.
								void navigate({
									search: previous => ({ ...previous, q: text, page: undefined }),
									replace: true,
								});
							}}
						/>
					</div>
				}
			>
				<FilterPill
					active={kind === undefined}
					count={everyKind}
					onClick={() => {
						showKind(undefined);
					}}
				>
					All
				</FilterPill>
				{PARTY_KINDS.map(option => (
					<FilterPill
						key={option}
						active={kind === option}
						count={directoryPage.counts[option]}
						onClick={() => {
							showKind(kind === option ? undefined : option);
						}}
					>
						{PARTY_KIND_LABELS[option]}
					</FilterPill>
				))}
			</Toolbar>
			<Card>
				<CardHeader
					title={`${total} ${total === 1 ? "party" : "parties"}`}
					action={<Updating active={isUpdating} />}
				/>
				{total === 0 ? (
					<Empty>{q.trim() === "" ? "No parties yet." : "No party's name holds your search."}</Empty>
				) : (
					<Table
						label="Parties"
						columns={columns}
						rows={directoryPage.parties}
						rowKey={entry => `${entry.kind}#${entry.id}`}
						interactiveRows
					/>
				)}
				{directoryPage.next === null && directoryPage.previous === null ? null : (
					<Pager
						label="Directory pages"
						previous={
							directoryPage.previous === null ? null : (
								<Link
									to="/directory"
									search={{ kind, q, page: directoryPage.previous }}
									className={buttonClassName({ size: "sm" })}
								>
									Previous
								</Link>
							)
						}
						next={
							directoryPage.next === null ? null : (
								<Link
									to="/directory"
									search={{ kind, q, page: directoryPage.next }}
									className={buttonClassName({ size: "sm" })}
								>
									Next
								</Link>
							)
						}
					/>
				)}
			</Card>
			{relatedParties.length === 0 ? null : (
				<Card className="mt-[22px]">
					<CardHeader title="Related" />
					<Table
						label="Related parties"
						columns={columns}
						rows={relatedParties}
						rowKey={entry => `${entry.kind}#${entry.id}`}
						interactiveRows
					/>
				</Card>
			)}
			<AddPartyDrawer open={isAdding} onClose={closeDrawer} />
		</>
	);
}

function DirectoryPage() {
	const { member } = useSession();
	return member.access.directory === "none" ? (
		<NoAccess description={DIRECTORY_DESCRIPTION} />
	) : (
		<PartyList canAdd={member.access.directory === "editor"} />
	);
}
